import React, { useMemo, useState } from 'react';
import _get from 'lodash/get';
import _classNames from 'classnames';
import { BLOCKS } from '@contentful/rich-text-types';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Mixpanel from 'utils/mixpanelService';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import SingleQuestion from 'components/Survey/SingleQuestion';
import { CheckIcon } from 'containers/Series/components/icons/CheckIcon';
import { downloadPdfFile } from 'containers/Course/utils';
import {
  createSteps,
  getDescription,
  getNextProgressIdx,
  isViewQuestionAnswered,
  parsePlaceholder,
} from './utils';
import { CourseActions } from '../CourseActions';

const useStyles = makeStyles(theme => ({
  richTextPane: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #C0BFBF',
    borderRadius: '12px',
    padding: props => (props.small ? theme.spacing(2) : theme.spacing(5)),
  },
  richText: {
    '& p, h6': {
      fontSize: props => props.fontSize,
      '& b': {
        ...theme.typography.subtitle1,
      },
    },
  },
  button: {
    flexGrow: 1,
    minHeight: props => !props.small && 48,
    padding: props => (props.small ? '6px 8px' : '10px 16px'),
    boxShadow: 'none',
    marginRight: 2,
    ...theme.typography.pRegular,
    textTransform: 'none',

    fontSize: props => (props.small ? '0.75rem' : '1rem'),

    '&,&.active:hover': {
      color: '#fff',
      border: '1px solid #37BB7C',
      backgroundColor: '#37BB7C',
    },

    '&.active': {
      fontWeight: 600,
    },

    '&:first-child': {
      borderRadius: '8px 0px 0px 8px',
    },

    '&:last-child': {
      borderRadius: '0px 8px 8px 0px',
      marginRight: 0,
    },

    '&:not(.Mui-disabled):not(.active):hover': {
      border: '1px solid #37BB7C',
      color: '#37BB7C',
    },
  },
  singleQuestion: {
    padding: 0,

    '& .MuiInputBase-multiline': {
      width: '100%',
    },
  },
  actionButton: {
    borderRadius: 8,
    padding: '10px 16px',
    marginTop: theme.spacing(2),
  },
  continueButton: {
    marginLeft: 'auto',
  },
}));

export const CourseWorksheet = ({
  item,
  coursesSiteCopy,
  seriesSiteCopy,
  worksheetProgress,
  completeResource,
  saveWorksheetProgress,
  completedResourcesIds,
  fontSize,
  onNext,
  handleCompleteChange,
  isSeriesComplete,
  trackingProps,
  questionRenderers = {},
  renderAnswer = () => null,
  small,
}) => {
  const classes = useStyles({ fontSize, small });
  const currentStep = _get(worksheetProgress, 'currentStep', 0);
  const currentViewIdx = _get(worksheetProgress, 'currentView', 0);
  const steps = useMemo(() => createSteps(item), [item]);
  const areStepsComplete = currentStep === steps.length - 1;
  const completed = completedResourcesIds.includes(item.sys.id);
  const [activeStep, setActiveStep] = useState(currentStep);
  const [activeViewIdx, setActiveViewIdx] = useState(currentViewIdx);
  const [answers, setAnswers] = useState(
    _get(worksheetProgress, 'answers', {}),
  );

  const description = getDescription(item);
  const stepData = steps[activeStep];
  const isLastStep =
    activeStep === steps.length - 1 &&
    stepData.views.length - 1 === activeViewIdx;

  const handleSaveAnswers = () => {
    const { nextViewIdx, nextStepIdx } = getNextProgressIdx({
      steps,
      activeStep,
      activeViewIdx,
      isLastStep,
    });
    const stepsPayload =
      currentStep > activeStep
        ? { currentStep, currentView: currentViewIdx }
        : {
            currentStep: nextStepIdx,
            currentView: nextViewIdx,
          };
    setActiveStep(nextStepIdx);
    setActiveViewIdx(nextViewIdx);
    saveWorksheetProgress(answers, stepsPayload);

    if (isLastStep) {
      completeResource(item, true, {
        autoCompleteSeries: true,
        shouldTransition: true,
      });
      Mixpanel.track('Course - Worksheet - Completed', trackingProps);
    }
  };

  const onAnswerChange = (name, value) => {
    setAnswers({ ...answers, [name]: value });
  };

  const onStepChange = idx => {
    setActiveStep(idx);
    setActiveViewIdx(0);
  };

  const onBack = () => {
    const prevView = activeViewIdx - 1;
    const prevStep = activeStep - 1;

    if (steps[activeStep].views[prevView]) {
      setActiveViewIdx(prevView);
    } else {
      const data = steps[prevStep];
      setActiveStep(activeStep - 1);
      setActiveViewIdx(data.views.length - 1);
    }
  };

  const onCheckboxChange = e => {
    handleCompleteChange(item, e.target.checked);
  };

  const handleDownloadPdf = () => {
    downloadPdfFile(pdf);
    Mixpanel.track('Course - Worksheet - Downloaded', trackingProps);
  };

  const surveyContextProps = {
    error: [],
    answers,
    onAnswerChange,
  };

  const renderParagraph = (_, children) => {
    let component;
    const mapped = children.map(child => {
      if (typeof child === 'string' && child.trimStart().startsWith('#')) {
        const { questionId, isAnswer } = parsePlaceholder(child);
        if (isAnswer) {
          return renderAnswer(answers[questionId]);
        }

        const question = _get(stepData, 'questions', []).find(
          q => q.name === questionId,
        );
        component = 'div';

        return (
          questionRenderers[question.type]?.({
            question,
            answers,
            onAnswerChange,
          }) || (
            <SingleQuestion
              key={question.name}
              className={classes.singleQuestion}
              number={0}
              surveyContextProps={surveyContextProps}
              withinPanel
              {...question}
            />
          )
        );
      }

      return child;
    });

    return (
      <Typography component={component} variant="body1" color="textPrimary">
        {mapped}
      </Typography>
    );
  };

  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: renderParagraph,
    },
  };

  const isContinueEnabled = isViewQuestionAnswered({
    answers,
    activeViewIdx,
    stepData,
  });
  const currentView =
    _get(stepData, ['views', activeViewIdx]) || _get(stepData, ['views', 0]);
  const pdf = _get(item.fields, 'asset');
  const showPrevButton = activeStep > 0 || activeViewIdx > 0;

  return (
    <div>
      <Box display="flex" justifyContent="space-between">
        <Typography
          variant={small ? 'h6' : 'h4'}
          color="textPrimary"
          gutterBottom
        >
          {_get(item.fields, 'title')}
        </Typography>
      </Box>
      <RichTextReactRenderer className={classes.richText} data={description} />
      <CourseActions
        mt={2}
        pdf={pdf}
        pdfLabel={_get(coursesSiteCopy, 'pageCopy.downloadWorksheet')}
        handleDownloadPdf={handleDownloadPdf}
        seriesSiteCopy={seriesSiteCopy}
        isSeriesComplete={isSeriesComplete}
        completed={completed}
        handleNext={() => onNext()}
        onCheckboxChange={onCheckboxChange}
        completedResourcesIds={completedResourcesIds}
        fontSize={fontSize}
        small={small}
      />
      <Box display="flex" mb={2} mt={2}>
        {steps.map((step, idx) => {
          const isCompleted = areStepsComplete || currentStep > idx;
          const disabled = idx > currentStep;
          return (
            <Button
              key={step.name}
              className={_classNames(classes.button, {
                active: activeStep === idx,
                completed: isCompleted,
              })}
              disabled={disabled}
              color={disabled ? 'default' : 'primary'}
              variant="outlined"
              onClick={() => onStepChange(idx)}
              startIcon={
                isCompleted ? (
                  <CheckIcon
                    transparent
                    color={null}
                    className={classes.checkIcon}
                    width={small ? 14 : undefined}
                    height={small ? 14 : undefined}
                  />
                ) : null
              }
            >
              {idx + 1}.{step.name}
            </Button>
          );
        })}
      </Box>
      {currentView && (
        <div className={classes.richTextPane}>
          <RichTextReactRenderer
            className={classes.richText}
            key={stepData.name}
            data={currentView}
            options={options}
            withHeaderStyles
          />
          <Box display="flex">
            {showPrevButton && (
              <Button
                variant="contained"
                color="primary"
                className={classes.actionButton}
                onClick={onBack}
              >
                {_get(coursesSiteCopy, 'pageCopy.back')}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              className={_classNames(
                classes.actionButton,
                classes.continueButton,
              )}
              endIcon={<ChevronRightIcon />}
              onClick={handleSaveAnswers}
              disabled={!isContinueEnabled}
            >
              {isLastStep
                ? _get(seriesSiteCopy, 'pageCopy.complete')
                : _get(seriesSiteCopy, 'pageCopy.continueCTA')}
            </Button>
          </Box>
        </div>
      )}
    </div>
  );
};
